// Ortak yardımcı fonksiyonlar

// Tehlike sınıfına göre belge geçerlilik süreleri (yıl cinsinden)
// Türkiye İSG mevzuatına göre
export const VALIDITY_PERIODS = {
  az_tehlikeli: {
    isg_egitimi: 3,      // 3 yıl
    saglik_raporu: 5     // 5 yıl
  },
  tehlikeli: {
    isg_egitimi: 2,      // 2 yıl
    saglik_raporu: 3     // 3 yıl
  },
  cok_tehlikeli: {
    isg_egitimi: 1,      // 1 yıl
    saglik_raporu: 1     // 1 yıl
  }
};

// Tehlike sınıfına göre belge geçerlilik tarihini hesapla
export const calculateValidityDate = (docKey, hazardClass, startDate) => {
  if (!startDate) return null;
  
  const periods = VALIDITY_PERIODS[hazardClass] || VALIDITY_PERIODS['tehlikeli'];
  const years = periods[docKey];
  
  if (!years) return null; // Bu belge için geçerlilik süresi tanımlı değil
  
  const date = new Date(startDate);
  date.setFullYear(date.getFullYear() + years);
  
  // YYYY-MM-DD formatında döndür
  return date.toISOString().split('T')[0];
};

// Tehlike sınıfına göre geçerlilik süresini metin olarak al
export const getValidityPeriodText = (docKey, hazardClass) => {
  const periods = VALIDITY_PERIODS[hazardClass] || VALIDITY_PERIODS['tehlikeli'];
  const years = periods[docKey];
  
  if (!years) return null;
  return `${years} yıl`;
};

// Tarih formatı: DD/MM/YY
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year.slice(-2)}`;
};

// Highlight matching text for search
export const highlightMatch = (text, query) => {
  if (!query || query.length < 3) return text;
  const regex = new RegExp(`(${query})`, 'gi');
  const parts = String(text).split(regex);
  return parts;
};

// T.C. Kimlik No Doğrulama Fonksiyonu
export const validateTcKimlikNo = (tc) => {
  if (!tc) return { isValid: true, error: null };
  
  if (tc.length !== 11) {
    return { isValid: false, error: 'T.C. Kimlik No 11 haneli olmalidir' };
  }
  
  if (!/^\d+$/.test(tc)) {
    return { isValid: false, error: 'T.C. Kimlik No sadece rakamlardan olusmalidir' };
  }
  
  if (tc[0] === '0') {
    return { isValid: false, error: 'T.C. Kimlik No sifir ile baslayamaz' };
  }
  
  const digits = tc.split('').map(Number);
  const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
  const evenSum = digits[1] + digits[3] + digits[5] + digits[7];
  
  if ((oddSum * 7 - evenSum) % 10 !== digits[9]) {
    return { isValid: false, error: 'Gecersiz T.C. Kimlik No' };
  }
  
  const firstTenSum = digits.slice(0, 10).reduce((a, b) => a + b, 0);
  if (firstTenSum % 10 !== digits[10]) {
    return { isValid: false, error: 'Gecersiz T.C. Kimlik No' };
  }
  
  return { isValid: true, error: null };
};

// Belge geçerlilik tarihini kontrol et
export const isDocumentExpired = (documents, docKey) => {
  const validityKey = `${docKey}_validity`;
  const dateKey = `${docKey}_date`;
  
  let expiryDate = documents?.[validityKey] || (docKey === 'mesleki_yeterlilik' ? documents?.[dateKey] : null);
  
  if (!expiryDate) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = new Date(expiryDate);
  
  return expDate < today;
};

// Belgenin gerçek statüsünü al (süre kontrolü dahil)
export const getActualDocStatus = (documents, docKey) => {
  const status = documents?.[docKey] || 'beklemede';
  
  if (status === 'onay' && isDocumentExpired(documents, docKey)) {
    return 'suresi_doldu';
  }
  
  return status;
};

// Genel statü hesaplama kuralları:
// 1. K.D. (kapsam_disi) = etkisiz eleman, hesaplamaya dahil edilmez
// 2. Herhangi bir belge "eksik" veya "suresi_doldu" ise → Genel: Eksik
// 3. Tüm belgeler "beklemede" ise (K.D. fark etmez) → Genel: Beklemede
// 4. Tüm belgeler "onay" veya "kapsam_disi" ise → Genel: Onaylı
export const calculateOverallStatus = (documents, docKeys, checkExpiry = true) => {
  if (!documents) return { status: 'beklemede', label: 'Beklemede', color: 'bg-amber-100 text-amber-700' };
  
  let hasEksikOrExpired = false;
  let hasBeklemede = false;
  let hasOnay = false;
  let relevantDocCount = 0; // K.D. hariç belge sayısı
  
  for (const key of docKeys) {
    const status = checkExpiry ? getActualDocStatus(documents, key) : (documents[key] || 'beklemede');
    
    // K.D. (kapsam_disi) etkisiz eleman - hesaplamaya dahil etme
    if (status === 'kapsam_disi') {
      continue;
    }
    
    relevantDocCount++;
    
    if (status === 'suresi_doldu' || status === 'eksik') {
      hasEksikOrExpired = true;
    } else if (status === 'beklemede') {
      hasBeklemede = true;
    } else if (status === 'onay') {
      hasOnay = true;
    }
  }
  
  // Hiç relevant belge yoksa (tümü K.D.) → Onaylı
  if (relevantDocCount === 0) {
    return { status: 'onay', label: 'Onaylı', color: 'bg-emerald-100 text-emerald-700' };
  }
  
  // Kural 2: Herhangi bir belge eksik veya süresi dolmuş ise → Eksik
  if (hasEksikOrExpired) {
    return { status: 'eksik', label: 'Eksik', color: 'bg-rose-100 text-rose-700' };
  }
  
  // Kural 3: Beklemede olan var ise → Beklemede
  if (hasBeklemede) {
    return { status: 'beklemede', label: 'Beklemede', color: 'bg-amber-100 text-amber-700' };
  }
  
  // Kural 4: Tümü onay ise → Onaylı
  if (hasOnay) {
    return { status: 'onay', label: 'Onaylı', color: 'bg-emerald-100 text-emerald-700' };
  }
  
  return { status: 'beklemede', label: 'Beklemede', color: 'bg-amber-100 text-amber-700' };
};

// Bugünün tarihini YYYY-MM-DD formatında al
export const getTodayString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// T.C. numarasını maskele: 41281522386 -> 412*****86
export const maskTcNo = (tc) => {
  if (!tc || tc.length < 5) return tc || '-';
  return tc.slice(0, 3) + '*****' + tc.slice(-2);
};

// Onay engeli: geçerlilik bugünden veya talep bitiş tarihinden önce bitiyorsa mesaj döner
export const validityBlockMessage = (validity, endDate) => {
  if (!validity) return null;
  const v = validity.slice(0, 10);
  const today = new Date().toLocaleDateString('sv-SE');
  if (v < today) return `Belgenin geçerlilik tarihi (${formatDate(v)}) bugün itibarıyla geçmiş. Onay verilemez.`;
  if (endDate && v < endDate.slice(0, 10)) return `Belgenin geçerlilik tarihi (${formatDate(v)}), talep bitiş tarihinden (${formatDate(endDate)}) önce sona eriyor. Onay verilemez.`;
  return null;
};
